import { FC, useCallback, useMemo, useState } from "react";
import { MDSButton, MDSButtonVariant, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { electronModule } from "@/modules/electron";
import { RecallPermissionKind } from "@/modules/electron/types";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { sleep } from "@/domains/event-loop/synchronization";
import {
  ConfigurationList,
  ConfigurationListItem,
  ConfigurationListItemKind,
  ConfigurationPermissionStatus,
} from "@/components/configuration";
import {
  FooterContainer,
  StepContent,
  StepTitle,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.styles";

type Props = {
  onNext: ({ autoMeetingDetectionEnabled }: { autoMeetingDetectionEnabled: boolean }) => void;
  hasNextStep: boolean;
  ignorePermissions: boolean;
  resolveConfigurationStatus: ({
    kind,
  }: {
    kind: RecallPermissionKind;
  }) => ConfigurationPermissionStatus;
  handleEnablePermission: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  handleOpenPermissionPane: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
};

export const OnboardingMeetingsPermissionsSetup: FC<Props> = ({
  onNext,
  hasNextStep,
  ignorePermissions,
  resolveConfigurationStatus,
  handleEnablePermission,
  handleOpenPermissionPane,
}) => {
  const [autoMeetingDetectionEnabled, setAutoMeetingDetectionEnabled] = useState(true);
  const [nextLoading, setNextLoading] = useState(false);

  const accessibilityEnabled =
    resolveConfigurationStatus({ kind: RecallPermissionKind.Accessibility }) ===
    ConfigurationPermissionStatus.Granted;
  const isNextDisabled = !ignorePermissions && autoMeetingDetectionEnabled && !accessibilityEnabled;

  const handleNext = useCallback(async () => {
    setNextLoading(true);

    try {
      await electronModule.setMeetingNotificationEnabled({ enabled: autoMeetingDetectionEnabled });

      // We include a short sleep to make the loading state more visible/less flashing
      await sleep(1000);
    } catch (error) {
      logger.error({
        message: "[OnboardingMeetingsPermissionsSetup] Failed to enable meeting notifications",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    } finally {
      setNextLoading(false);
    }

    onNext({ autoMeetingDetectionEnabled });
  }, [autoMeetingDetectionEnabled, onNext]);

  const items = useMemo<ConfigurationListItem[]>(() => {
    const itemList: ConfigurationListItem[] = [
      {
        kind: ConfigurationListItemKind.Toggle,
        id: "auto-meeting-detection",
        title: (
          <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Medium}>
            Enable one-click voice notes anytime a meeting is detected
          </MDSText>
        ),
        checked: autoMeetingDetectionEnabled,
        onChange: setAutoMeetingDetectionEnabled,
      },
    ];

    if (autoMeetingDetectionEnabled && !ignorePermissions) {
      itemList.push({ kind: ConfigurationListItemKind.Divider, id: "divider-accessibility" });

      itemList.push({
        kind: ConfigurationListItemKind.Permission,
        id: "accessibility",
        title: "Accessibility",
        description: "Mem relies on accessibility features to detect when you've joined a meeting",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.Accessibility }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.Accessibility,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.Accessibility,
          }),
      });
    }

    return itemList;
  }, [
    autoMeetingDetectionEnabled,
    ignorePermissions,
    resolveConfigurationStatus,
    handleEnablePermission,
    handleOpenPermissionPane,
  ]);

  return (
    <WizardContentWrapper>
      <StepTitle>Enable meeting notifications</StepTitle>
      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          Mem will let you start voice mode with a single click any time a meeting is detected
        </MDSText>

        <ConfigurationList items={items} />
      </StepContent>
      <FooterContainer align="flex-end">
        <MDSButton
          variant={MDSButtonVariant.Contrast}
          onClick={handleNext}
          isDisabled={isNextDisabled}
          isLoading={nextLoading}
        >
          {hasNextStep ? "Next" : "Finish"}
        </MDSButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
};
